import { GenericTable, POSButton } from '@publicisgroupe/p-os-components';
import React, { useCallback, useMemo, useState } from 'react';
import { Entity } from '../../interfaces/Entity';
import { Field, XdmContextInfo } from '../../interfaces/Field';
import { getValueOfXdmField } from '../../utils/valueFormatUtils';
import { calculateFieldsDependencies } from '../../utils/xdmContextUtils';
import './XdmContextView.less';
import { XdmContextItemModal } from '../XdmContextItemModal/XdmContextItemModal';
import { v4 as uuidv4 } from 'uuid';

type XdmContextViewProps = {
  field: Field;
  value: any;
  readonly?: boolean;
  onValueChanged?: (newValue: any) => void;
};

export const XdmContextView: React.FC<XdmContextViewProps> = ({
  field,
  value,
  readonly = true,
  onValueChanged,
}) => {
  const [rowToEdit, setRowToEdit] = useState<any>(null);

  const xdmContextInfo = useMemo(() => {
    return field.xdmContext as XdmContextInfo;
  }, [field]);

  const fieldsDependencies = useMemo(() => {
    return calculateFieldsDependencies(xdmContextInfo).sort((a, b) =>
      a.up.length > b.up.length ? 1 : -1
    );
  }, [xdmContextInfo]);

  const rows = useMemo(() => {
    if (!value) {
      return [];
    }

    return Array.isArray(value) ? value : [value];
  }, [value]);

  const columns = useMemo<any>(() => {
    return (
      fieldsDependencies.map((f) => {
        return {
          name: f.field,
          selector: f.field,
          sortable: false,
          cell: (item: Entity) => {
            return getValueOfXdmField(f.fieldInfo, item[f.field]);
          },
        };
      }) ?? []
    );
  }, [fieldsDependencies]);

  const handleAddRow = useCallback(() => {
    setRowToEdit({});
  }, []);

  const handleDeleteRow = useCallback(
    (item: any) => {
      onValueChanged &&
        onValueChanged(value.filter((i: any) => i.id !== item.id));
    },
    [onValueChanged, value]
  );

  const handleNewValue = useCallback(
    (newValue: any) => {
      setRowToEdit(null);
      if (!field.isArray) {
        if (newValue.id) {
          onValueChanged && onValueChanged(newValue);
        } else {
          onValueChanged && onValueChanged({ ...newValue, id: uuidv4() });
        }

        return;
      }

      if (newValue.id) {
        onValueChanged &&
          onValueChanged(
            value.map((i: any) => {
              if (i.id === newValue.id) {
                return newValue;
              }

              return i;
            })
          );
      } else {
        onValueChanged &&
          onValueChanged([...(value || []), { ...newValue, id: uuidv4() }]);
      }
    },
    [field.isArray, onValueChanged, value]
  );

  return (
    <div className="xdm-context-view">
      {!readonly && (
        <div>
          <POSButton secondary={true} floated="right" onClick={handleAddRow}>
            Add row
          </POSButton>
        </div>
      )}
      <GenericTable
        getId={(x) => x.id}
        columnDefinitions={columns ?? []}
        className="p-os-grid"
        data={rows ?? []}
        onSort={() => null}
        hidePagination={true}
        pageCount={0}
        pageIndex={0}
        onPageChange={() => null}
        actions={
          readonly
            ? undefined
            : {
                orientation: 'horizontal',
                actions: [
                  {
                    icon: 'delete',
                    onClick: handleDeleteRow,
                    tooltipContent: 'Delete',
                  },
                  {
                    icon: 'edit',
                    onClick: setRowToEdit,
                    tooltipContent: 'Edit',
                  },
                ],
              }
        }
      />
      {rowToEdit && (
        <XdmContextItemModal
          field={field}
          initialValues={rowToEdit}
          onClose={() => setRowToEdit(null)}
          onSubmit={handleNewValue}
        />
      )}
    </div>
  );
};
